(() => {
  const DESKTOP_QUERY = '(min-width: 750px)';

  const submitDesktopFilter = (form) => {
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }

    form.submit();
  };

  const initialiseSection = (section) => {
    if (!section || section.dataset.collectionFiltersReady === 'true') return;
    section.dataset.collectionFiltersReady = 'true';

    const desktopForm = section.querySelector('[data-collection-filter-form]');
    let priceSubmitTimer;

    desktopForm?.addEventListener('change', (event) => {
      if (!window.matchMedia(DESKTOP_QUERY).matches) return;

      if (event.target.matches('input[type="number"]')) {
        window.clearTimeout(priceSubmitTimer);
        priceSubmitTimer = window.setTimeout(() => submitDesktopFilter(desktopForm), 450);
        return;
      }

      if (event.target.matches('input[type="checkbox"]')) {
        submitDesktopFilter(desktopForm);
      }
    });

    const trigger = section.querySelector('[data-collection-filter-trigger]');
    const dialog = section.querySelector('[data-collection-filter-dialog]');
    if (!trigger || !dialog) return;

    const closeButton = dialog.querySelector('[data-collection-filter-close]');
    const menu = dialog.querySelector('[data-mobile-filter-menu]');
    const panelTriggers = [...dialog.querySelectorAll('[data-mobile-filter-open]')];
    const panels = [...dialog.querySelectorAll('[data-mobile-filter-panel]')];
    const panelTransition = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 360;
    const panelHideTimers = new WeakMap();
    const panelFrameJobs = new WeakMap();
    let activePanel = null;

    const cancelPanelHide = (panel) => {
      const timer = panelHideTimers.get(panel);
      if (timer === undefined) return;
      window.clearTimeout(timer);
      panelHideTimers.delete(panel);
    };

    const cancelPanelFrame = (panel) => {
      const frame = panelFrameJobs.get(panel);
      if (frame === undefined) return;
      window.cancelAnimationFrame(frame);
      panelFrameJobs.delete(panel);
    };

    const closePanel = ({ restoreFocus = true } = {}) => {
      if (!activePanel) return;

      const panel = activePanel;
      const panelIndex = panel.dataset.mobileFilterPanel;
      const panelTrigger = panelTriggers.find(
        (candidate) => candidate.dataset.mobileFilterOpen === panelIndex,
      );

      cancelPanelHide(panel);
      cancelPanelFrame(panel);
      panel.classList.remove('is-active');
      panel.setAttribute('inert', '');
      panelTrigger?.setAttribute('aria-expanded', 'false');
      menu?.removeAttribute('aria-hidden');
      menu?.removeAttribute('inert');
      activePanel = null;

      const timer = window.setTimeout(() => {
        panelHideTimers.delete(panel);
        if (activePanel !== panel && !panel.classList.contains('is-active')) panel.hidden = true;
      }, panelTransition);
      panelHideTimers.set(panel, timer);

      if (restoreFocus) panelTrigger?.focus({ preventScroll: true });
    };

    const openPanel = (panelTrigger) => {
      const panel = panels.find(
        (candidate) => candidate.dataset.mobileFilterPanel === panelTrigger.dataset.mobileFilterOpen,
      );
      if (!panel) return;

      if (activePanel && activePanel !== panel) closePanel({ restoreFocus: false });

      cancelPanelHide(panel);
      cancelPanelFrame(panel);
      activePanel = panel;
      panel.hidden = false;
      panel.removeAttribute('inert');
      panelTrigger.setAttribute('aria-expanded', 'true');

      // Force Safari to paint the off-canvas start state before activating it.
      void panel.offsetWidth;
      const frame = window.requestAnimationFrame(() => {
        panelFrameJobs.delete(panel);
        if (activePanel !== panel || panel.hidden) return;
        panel.classList.add('is-active');
        panel.querySelector('[data-mobile-filter-back]')?.focus({ preventScroll: true });
        menu?.setAttribute('aria-hidden', 'true');
        menu?.setAttribute('inert', '');
      });
      panelFrameJobs.set(panel, frame);
    };

    const resetDrawer = () => {
      panels.forEach((panel) => {
        cancelPanelHide(panel);
        cancelPanelFrame(panel);
        panel.classList.remove('is-active');
        panel.setAttribute('inert', '');
        panel.hidden = true;
      });
      panelTriggers.forEach((panelTrigger) => panelTrigger.setAttribute('aria-expanded', 'false'));
      menu?.removeAttribute('aria-hidden');
      menu?.removeAttribute('inert');
      activePanel = null;
    };

    const openDrawer = () => {
      resetDrawer();
      trigger.setAttribute('aria-expanded', 'true');
      if (window.ArcticTheme?.openDialog) {
        window.ArcticTheme.openDialog(dialog, trigger);
      } else if (typeof dialog.showModal === 'function') {
        dialog.showModal();
        dialog.dataset.dialogState = 'open';
      } else {
        dialog.setAttribute('open', '');
        dialog.dataset.dialogState = 'open';
      }
    };

    const closeDrawer = () => {
      trigger.setAttribute('aria-expanded', 'false');
      if (window.ArcticTheme?.closeDialog) {
        window.ArcticTheme.closeDialog(dialog);
      } else if (typeof dialog.close === 'function') {
        dialog.close();
        dialog.removeAttribute('data-dialog-state');
      } else {
        dialog.removeAttribute('open');
        dialog.removeAttribute('data-dialog-state');
      }
    };

    trigger.addEventListener('click', openDrawer);
    closeButton?.addEventListener('click', closeDrawer);
    panelTriggers.forEach((panelTrigger) => {
      panelTrigger.addEventListener('click', () => openPanel(panelTrigger));
    });
    panels.forEach((panel) => {
      panel.querySelector('[data-mobile-filter-back]')?.addEventListener('click', () => closePanel());
    });

    dialog.addEventListener('close', () => {
      trigger.setAttribute('aria-expanded', 'false');
      resetDrawer();
    });

    const desktopMediaQuery = window.matchMedia(DESKTOP_QUERY);
    const handleDesktopChange = (event) => {
      if (event.matches && dialog.hasAttribute('open')) closeDrawer();
    };
    if (typeof desktopMediaQuery.addEventListener === 'function') {
      desktopMediaQuery.addEventListener('change', handleDesktopChange);
    } else {
      desktopMediaQuery.addListener(handleDesktopChange);
    }

    window.ArcticTheme?.initialize(section);
  };

  const initialiseAll = (scope = document) => {
    if (scope.matches?.('[data-collection-section]')) initialiseSection(scope);
    scope.querySelectorAll?.('[data-collection-section]').forEach(initialiseSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
